import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { adminListUsers, adminSetUserRole } from '../lib/supabase'

const NAVY = '#0F2A4A'
const SILVER = '#64748B'
const ROLE_OPTIONS = ['user', 'moderator', 'admin']

function StatusPill({ ok, trueLabel, falseLabel }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      backgroundColor: ok ? '#DCFCE7' : '#FEF2F2',
      color: ok ? '#166534' : '#991B1B',
    }}>
      {ok ? trueLabel : falseLabel}
    </span>
  )
}

export default function Dashboard() {
  const { user } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [savingId, setSavingId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await adminListUsers()
      setUsers(data ?? [])
    } catch (err) {
      setError(err.message || 'Failed to load users.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleRoleChange = async (targetUserId, newRole) => {
    setSavingId(targetUserId)
    setError('')
    try {
      await adminSetUserRole(targetUserId, newRole)
      setUsers(previous => previous.map(entry => (
        entry.user_id === targetUserId ? { ...entry, role: newRole } : entry
      )))
    } catch (err) {
      setError(err.message || 'Failed to update role.')
    } finally {
      setSavingId(null)
    }
  }

  const acceptedCount = users.filter(entry => entry.beta_agreement_accepted).length

  return (
    <div className="section max-w-5xl" style={{ padding: '2rem 1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: NAVY, margin: 0 }}>User Management</h1>
          <p style={{ color: SILVER, fontSize: 14, marginTop: 4 }}>
            Signed in as {user?.email} - {users.length} user{users.length === 1 ? '' : 's'} - {acceptedCount} agreement acceptance{acceptedCount === 1 ? '' : 's'}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: 8,
            border: `1.5px solid ${NAVY}`,
            backgroundColor: 'white',
            color: NAVY,
            fontWeight: 600,
            fontSize: 13,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: 8,
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          color: '#991B1B',
          fontSize: 14,
        }}>
          {error}
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid #E2E8F0', borderRadius: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ backgroundColor: '#F1F5F9', textAlign: 'left' }}>
              <th style={{ padding: '10px 14px' }}>Email</th>
              <th style={{ padding: '10px 14px' }}>Joined</th>
              <th style={{ padding: '10px 14px' }}>Last sign-in</th>
              <th style={{ padding: '10px 14px' }}>Beta agreement</th>
              <th style={{ padding: '10px 14px' }}>Role</th>
            </tr>
          </thead>
          <tbody>
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 16, textAlign: 'center', color: SILVER }}>No users found.</td>
              </tr>
            )}
            {users.map(entry => (
              <tr key={entry.user_id} style={{ borderTop: '1px solid #E2E8F0' }}>
                <td style={{ padding: '10px 14px' }}>{entry.email}</td>
                <td style={{ padding: '10px 14px', color: SILVER }}>
                  {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '-'}
                </td>
                <td style={{ padding: '10px 14px', color: SILVER }}>
                  {entry.last_sign_in_at ? new Date(entry.last_sign_in_at).toLocaleDateString() : '-'}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <StatusPill ok={entry.beta_agreement_accepted} trueLabel="Accepted" falseLabel="Not accepted" />
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <select
                    value={entry.role}
                    disabled={savingId === entry.user_id || entry.user_id === user?.id}
                    onChange={event => handleRoleChange(entry.user_id, event.target.value)}
                    style={{
                      padding: '4px 8px',
                      borderRadius: 6,
                      border: '1.5px solid #CBD5E1',
                      fontSize: 13,
                      backgroundColor: 'white',
                      cursor: (savingId === entry.user_id || entry.user_id === user?.id) ? 'not-allowed' : 'pointer',
                    }}
                    title={entry.user_id === user?.id ? "You can't change your own role here." : undefined}
                  >
                    {ROLE_OPTIONS.map(role => <option key={role} value={role}>{role}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ color: SILVER, fontSize: 12, marginTop: 12 }}>
        Role changes take effect immediately and are enforced server-side through Row Level Security.
      </p>
    </div>
  )
}
